import React, {useMemo, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {Colors} from '../../../constants/colors'
import {AmlReportModel, ComplianceStatus, UserComplianceReportModel,} from '../../../redux/companyMonitorTypes'
import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Badge from 'react-bootstrap/Badge'
import {formatDateTimeLocale} from '../../../utils/dateHelper'
import UserComplianceDecisionList from './UserComplianceDecisionList'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileContract} from "@fortawesome/free-solid-svg-icons";
import ScoreBar from "./ScoreBar";

interface Props {
  items: UserComplianceReportModel[]
  onOpenAmlReportClick: (amlReport: AmlReportModel) => void
}

const UserComplianceReportList = ({items, onOpenAmlReportClick}: Props) => {
  const styles = useStyles()
  const [expanded, setExpanded] = useState<ExpandedState>({})

  const getComplianceBadge = (status: ComplianceStatus) => {
    switch (status) {
      case 'compliant':
        return (
          <Badge text="dark" bg="success">
            Compliant
          </Badge>
        )
      case 'nonCompliant':
        return (
          <Badge text="dark" bg="danger">
            Non-compliant
          </Badge>
        )
      case 'unknown':
      default:
        return (
          <Badge text="dark" bg="warning">
            Decision required
          </Badge>
        )
    }
  }

  const columns = useMemo<ColumnDef<UserComplianceReportModel>[]>(
    () => [
      {
        id: 'created',
        header: 'Created',
        cell: ({row}) => (
          <span>{formatDateTimeLocale(row.original.created)}</span>
        ),
      },
      {
        id: 'score',
        header: 'Score',
        cell: ({row}) => <ScoreBar score={row.original.score} format="decimal" />,
      },
      {
        id: 'reportedComplianceStatus',
        header: 'Reported compliance status',
        cell: ({row}) => (
          <div style={{display: 'flex', alignItems: 'center'}}>
            {getComplianceBadge(row.original.reportedComplianceStatus)}
          </div>
        ),
      },
      {
        id: 'reportedComplianceViolations',
        header: 'Reported violations',
        cell: ({row}) => (
          <span>{row.original.reportedComplianceViolations ?? '-'}</span>
        ),
      },
      {
        id: 'decisionCount',
        header: 'Decisions',
        cell: ({row}) => (
          <span>{row.original.complianceDecisions.length}</span>
        ),
      },
      {
        id: 'currentComplianceStatus',
        header: 'Current compliance decision',
        cell: ({row}) => (
          <div style={{display: 'flex', alignItems: 'center'}}>
            {getComplianceBadge(row.original.currentComplianceStatus)}
          </div>
        ),
      },
    ],
    []
  )

  const table = useReactTable({
    data: items ?? [],
    columns,
    state: {expanded},
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  })

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={styles.headerRow}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className={styles.headerCell}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr
                className={styles.bodyRow}
                onClick={() => row.toggleExpanded()}
                tabIndex={0}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={styles.bodyCell}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>
              {row.getIsExpanded() && (
                <tr>
                  <td
                    className={styles.expandedCell}
                    colSpan={row.getVisibleCells().length}
                  >
                    <div className={styles.detailsContainer}>
                      <div className={styles.detailsCard}>
                        <h6>AML Report basis</h6>
                        <FontAwesomeIcon
                          onClick={() => {
                            onOpenAmlReportClick(row.original.amlReport)
                          }}
                          className={styles.icon}
                          icon={faFileContract}
                          color={Colors.euro_600}
                          size="xl"
                          title="Open AML report"
                        />

                        <p>Created: <span>{formatDateTimeLocale(row.original.amlReport.created)}</span></p>
                        <p>Sanction
                          status: <span>{row.original.amlReport.isSanction ? 'Sanctioned' : "Not sanctioned"}</span></p>
                        <p>PEP status: <span>{row.original.amlReport.isPep ? "PEP" : "Not PEP"}</span></p>
                        <p>RCA status: <span>{row.original.amlReport.isRca ? "RCA" : "Not RCA"}</span></p>
                      </div>
                      <div className={styles.detailsCard}>
                        <UserComplianceDecisionList bg={Colors.grayCard} items={row.original.complianceDecisions}/>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default UserComplianceReportList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerRow: {
    backgroundColor: 'transparent',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    backgroundColor: Colors.darkGrayCard,
    borderRadius: '4px',
    fontWeight: 'bold',
    color: Colors.darkGray2,
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  bodyRow: {
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover $bodyCell': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    backgroundColor: Colors.darkGrayCard,
    transition: '0.4s',

    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  expandedCell: {
    paddingTop: 2,
    paddingBottom: 22,
    paddingLeft: 22,
    paddingRight: 2,
    backgroundColor: Colors.darkGrayCard,
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  detailsCard: {
    borderRadius: 6,
    padding: '15px 25px',
    backgroundColor: Colors.grayCard,
    position: 'relative',
  },
  icon: {
    position: 'absolute',
    top: 20,
    right: 20,
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro5,
    }
  }
})
